import { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp, ExternalLink, Loader2, Scale } from 'lucide-react';
import { api } from '../../api/client';
import { DefenderDrawer } from './DefenderDrawer';

interface Complaint {
  id: number;
  case_id: string;
  site_item_id: string;
  site_id: string | null;
  title: string | null;
  thumbnail: string | null;
  complainant: string | null;
  reason_id: string | null;
  reason_text: string | null;
  ml_status: string;
  due_date: string | null;
  sold_quantity: number;
  local_action: string | null;
}

interface ComplaintsData {
  total: number;
  cases: Complaint[];
}

const SITE_LABEL: Record<string, string> = {
  MLA: 'Argentina',
  MLM: 'México',
  MLC: 'Chile',
  MCO: 'Colombia',
  MLB: 'Brasil',
};

const SITE_DOMAIN: Record<string, string> = {
  MLA: 'articulo.mercadolibre.com.ar',
  MLM: 'articulo.mercadolibre.com.mx',
  MLC: 'articulo.mercadolibre.cl',
  MCO: 'articulo.mercadolibre.com.co',
  MLB: 'produto.mercadolivre.com.br',
};

const STATUS_LABEL: Record<string, string> = {
  WAITING_DOCUMENTATION: 'Esperando tu respuesta',
  DOCUMENTATION_PRESENTED: 'Respondida — espera al denunciante',
};

const REASON_LABEL: Record<string, string> = {
  PPPI1: 'Reclama producto falsificado',
  PPPI2: 'Reclama uso indebido de marca',
  PPPI5: 'Reclama uso de textos protegidos (descripción)',
  PPPI6: 'Reclama uso de imágenes protegidas',
};

function daysLeft(dueDate: string | null): number | null {
  if (!dueDate) return null;
  return Math.floor((new Date(dueDate).getTime() - Date.now()) / 86400000);
}

function DueBadge({ dueDate }: { dueDate: string | null }) {
  const d = daysLeft(dueDate);
  if (d === null) return null;
  const label =
    d < 0 ? 'Plazo vencido' : d === 0 ? 'Vence HOY' : d === 1 ? 'Vence mañana' : `Vence en ${d} días`;
  const style = d <= 1 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${style}`}>{label}</span>;
}

export function IpComplaintsBanner({ sellerId }: { sellerId: number }) {
  const [data, setData] = useState<ComplaintsData | null>(null);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, string>>({});
  const [defenderCase, setDefenderCase] = useState<Complaint | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<ComplaintsData>(`/ip-complaints?seller_id=${sellerId}`);
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      setData(null); // banner informativo: sin toast
    }
  }, [sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || data.total === 0) return null;

  const pending = data.cases.filter((c) => c.ml_status === 'WAITING_DOCUMENTATION');
  const minDays = Math.min(
    ...data.cases.map((c) => daysLeft(c.due_date) ?? 99)
  );
  const urgent = minDays <= 1;

  const withdraw = async (caseId: string) => {
    setBusy(caseId);
    setConfirming(null);
    try {
      const res = await api.post(`/ip-complaints/${caseId}/withdraw`, { seller_id: sellerId });
      setResult((r) => ({
        ...r,
        [caseId]: res.success ? 'ok' : 'partial',
      }));
      await load();
    } catch {
      setResult((r) => ({ ...r, [caseId]: 'error' }));
    } finally {
      setBusy(null);
    }
  };

  const markReview = async (caseId: string) => {
    try {
      await api.post(`/ip-complaints/${caseId}/review`, { seller_id: sellerId });
      await load();
    } catch {
      /* noop */
    }
  };

  return (
    <div
      className={`rounded-lg border mb-4 ${
        urgent ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'
      }`}
    >
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <ShieldAlert className={`w-5 h-5 shrink-0 ${urgent ? 'text-red-600' : 'text-amber-600'}`} />
        <div className="flex-1">
          <span className="font-semibold text-sm text-gray-900">
            {data.total} denuncia{data.total !== 1 ? 's' : ''} de propiedad intelectual abierta
            {data.total !== 1 ? 's' : ''}
          </span>
          <span className="ml-2 text-xs text-gray-600">
            {pending.length > 0
              ? `${pending.length} esperando respuesta · la más próxima ${
                  minDays <= 0 ? 'vence HOY' : minDays === 1 ? 'vence mañana' : `vence en ${minDays} días`
                }`
              : 'todas respondidas — esperando al denunciante'}
          </span>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 grid gap-3 md:grid-cols-2">
          {data.cases.map((c) => (
            <div key={c.case_id} className="bg-white rounded-lg border border-gray-200 p-3 flex gap-3">
              {c.thumbnail ? (
                <img
                  src={c.thumbnail}
                  alt=""
                  className="w-14 h-14 object-contain rounded shrink-0 bg-gray-50"
                />
              ) : (
                <div className="w-14 h-14 rounded bg-gray-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate" title={c.title ?? ''}>
                    {c.title ?? c.site_item_id}
                  </p>
                  <DueBadge dueDate={c.due_date} />
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  {SITE_LABEL[c.site_id ?? ''] ?? c.site_id} ·{' '}
                  {c.complainant ? `Denuncia: ${c.complainant}` : 'Denunciante s/d'} ·{' '}
                  {c.sold_quantity > 0 ? `${c.sold_quantity} venta(s) histórica(s)` : 'nunca vendió'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {REASON_LABEL[c.reason_id ?? ''] ?? c.reason_text ?? 'Motivo s/d'} ·{' '}
                  {STATUS_LABEL[c.ml_status] ?? c.ml_status}
                </p>

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {c.local_action === 'withdrawn' || result[c.case_id] === 'ok' ? (
                    <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">
                      ✓ Retirado y respondido
                    </span>
                  ) : busy === c.case_id ? (
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Retirando y verificando…
                    </span>
                  ) : confirming === c.case_id ? (
                    <>
                      <button
                        className="text-xs font-semibold bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        onClick={() => withdraw(c.case_id)}
                      >
                        Confirmar retiro definitivo
                      </button>
                      <button
                        className="text-xs text-gray-600 px-2 py-1 rounded hover:bg-gray-100"
                        onClick={() => setConfirming(null)}
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      {c.ml_status === 'WAITING_DOCUMENTATION' && (
                        <button
                          className="text-xs font-medium bg-gray-900 text-white px-2 py-1 rounded hover:bg-gray-700"
                          onClick={() => setConfirming(c.case_id)}
                        >
                          Retirar y responder
                        </button>
                      )}
                      <button
                        className="text-xs font-medium text-amber-700 border border-amber-300 bg-amber-50 px-2 py-1 rounded hover:bg-amber-100 flex items-center gap-1"
                        onClick={() => setDefenderCase(c)}
                      >
                        <Scale className="w-3 h-3" /> Defensor
                      </button>
                      {c.local_action !== 'review' && (
                        <button
                          className="text-xs text-gray-700 border border-gray-300 px-2 py-1 rounded hover:bg-gray-50"
                          onClick={() => markReview(c.case_id)}
                        >
                          Marcar para analizar
                        </button>
                      )}
                      {c.local_action === 'review' && (
                        <span className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded">
                          ⏸ Marcada para analizar
                        </span>
                      )}
                      {(c.local_action === 'withdraw_partial' || result[c.case_id] === 'partial') && (
                        <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                          ⚠ Retiro parcial — revisar
                        </span>
                      )}
                      {result[c.case_id] === 'error' && (
                        <span className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
                          Error — reintentar
                        </span>
                      )}
                    </>
                  )}
                  <a
                    className="text-xs text-gray-400 hover:text-gray-600 ml-auto flex items-center gap-0.5"
                    href={`https://${SITE_DOMAIN[c.site_id ?? ''] ?? SITE_DOMAIN.MLA}/${c.site_item_id.slice(0, 3)}-${c.site_item_id.slice(3)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver aviso <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {defenderCase && (
        <DefenderDrawer
          sellerId={sellerId}
          caseId={defenderCase.case_id}
          caseTitle={defenderCase.title ?? defenderCase.site_item_id}
          onSent={load}
          onClose={() => setDefenderCase(null)}
        />
      )}
    </div>
  );
}
